/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useResourceItem } from "@app/resources/hooks";
import {
  getLockedShortLabel,
  getViewLimitReachedTitle,
  isViewLimitReached,
} from "@app/resources/utils/resource-view-limit";
import { type Resource, ResourceType } from "@core/shared/types";
import { formatDuration } from "@core/shared/utils";
import { DownloadIcon, LockIcon } from "@design-system/components/Icons";
import { cn } from "@design-system/utils";
import { trans } from "@mongez/localization";
import { ClockIcon } from "lucide-react";
import { ResourceAssessment } from "./ResourceAssessment";
import { ResourceRemainingViews } from "./ResourceRemainingViews";
import { ResourceUrl } from "./ResourceUrl";
import { ResourceVideo } from "./ResourceVideo";
import { ResourceYoutubeVideo } from "./ResourceYoutubeVideo";

type ResourceItemProps = {
  resource: Resource;
  className?: string;
  isLinkedResource?: boolean;
};

export function ResourcesListItem({
  resource,
  className,
  isLinkedResource = false,
}: ResourceItemProps) {
  const {
    isExpanded,
    allLinkedResources,
    theme,
    title,
    type,
    duration,
    isLocked,
    getResourceIcon,
    getResourceIconStyle,
    getResourceTypeLabel,
    isInlineDisplayable,
    url,
    trackResourceView,
    handleClick,
  } = useResourceItem(resource);

  return (
    <div className={cn("space-y-3", className)}>
      <div
        className={cn(
          "group relative cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg hover:shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-primary/40 dark:hover:shadow-none",
          isLocked
            ? "border-gray-200 opacity-75 dark:border-gray-600"
            : `hover:bg-gradient-to-r hover:from-transparent hover:to-${theme.lightGradient.split(" ")[1]}/10 dark:hover:to-${theme.lightGradient.split(" ")[1]}/5`,
          isInlineDisplayable(type) &&
            `hover:bg-gradient-to-r hover:from-transparent hover:to-${theme.lightGradient.split(" ")[1]}/10 dark:hover:to-${theme.lightGradient.split(" ")[1]}/5`,
        )}
        onClick={handleClick}
      >
        <div className="flex items-center justify-between gap-4">
          {/* Left Side - Icon and Content */}
          <div className="flex min-w-0 flex-1 items-center gap-3.5">
            {/* Resource Type Icon */}
            <div className="flex-shrink-0">
              <div
                className={cn(
                  "flex h-11 w-11 items-center justify-center rounded-xl border shadow-sm",
                  isLocked
                    ? "border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400"
                    : `border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-700 ${getResourceIconStyle(type)}`,
                )}
              >
                {isLocked ? (
                  <LockIcon className="h-5 w-5" />
                ) : (
                  <div className="flex h-5 w-5 items-center justify-center">
                    {getResourceIcon(type)}
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
              <h4
                className={cn(
                  "mb-1.5 line-clamp-2 text-sm font-black leading-6 sm:text-base",
                  isLocked
                    ? "text-gray-500 dark:text-gray-400"
                    : "text-gray-800 group-hover:text-gray-900 dark:text-gray-100 dark:group-hover:text-gray-50",
                )}
              >
                {title || `${getResourceTypeLabel(type)} File`}
              </h4>

              {/* Additional Info */}
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                {duration && (
                  <div
                    className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 font-bold dark:bg-slate-800"
                    dir="ltr"
                  >
                    <ClockIcon className="h-3 w-3" />
                    <span className="font-medium">
                      {formatDuration(duration)}
                    </span>
                  </div>
                )}
                {isLocked && (
                  <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                    <LockIcon className="h-3 w-3" />
                    <span className="font-medium">
                      {getLockedShortLabel(resource)}
                    </span>
                  </div>
                )}
                <ResourceRemainingViews resource={resource} />
              </div>
            </div>
          </div>

          {/* Right Side - Type Tag and Action */}
          <div className="flex shrink-0 items-center gap-2">
            {/* Type Tag */}
            <div className="flex-shrink-0">
              <div
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold shadow-sm transition-all duration-200",
                  isLocked
                    ? "border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    : `bg-gradient-to-r ${theme.gradient} text-white ${theme.border}`,
                )}
              >
                {isLocked ? (
                  <LockIcon className="h-3 w-3" />
                ) : (
                  <div className="flex h-3 w-3 items-center justify-center">
                    {getResourceIcon(type)}
                  </div>
                )}
                <span>{getResourceTypeLabel(type)}</span>
              </div>
            </div>

            {/* Action Icon */}
            {!isLocked && !isInlineDisplayable(type) && (
              <div className="flex-shrink-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 shadow-sm transition-all duration-200 group-hover:scale-105 group-hover:bg-blue-100 group-hover:text-blue-600 dark:bg-slate-800 dark:text-slate-300 dark:group-hover:bg-blue-800/30 dark:group-hover:text-blue-400">
                  <DownloadIcon className="h-4 w-4" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Inline content for PDF and Image */}
      {isExpanded && isInlineDisplayable(type) && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/60 sm:p-4">
          {isLocked ? (
            // Show locked message and linked resources
            <div className="space-y-3">
              <div className="py-2 text-center sm:py-3">
                <LockIcon className="mx-auto mb-2 h-6 w-6 text-gray-400 dark:text-gray-500 sm:h-8 sm:w-8" />
                <h4 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {isViewLimitReached(resource)
                    ? getViewLimitReachedTitle(resource)
                    : trans("grades.resourceLocked")}
                </h4>
                {isViewLimitReached(resource) ? (
                  <p className="text-xs text-gray-600 dark:text-gray-300 sm:text-sm">
                    {trans("grades.viewLimitReachedHint")}
                  </p>
                ) : (
                  resource.lockReason && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 sm:text-sm">
                      {trans("grades." + resource.lockReason)}
                    </p>
                  )
                )}
              </div>

              {/* Display linked resources using the same UI as resource content */}
              {allLinkedResources.length > 0 && (
                <div className="space-y-2">
                  {allLinkedResources.map((linkedResource, index) => (
                    <div
                      key={linkedResource.id || index}
                      className="rounded border border-orange-200 bg-orange-50 p-2 dark:border-orange-700 dark:bg-orange-900/20 sm:p-3"
                    >
                      <ResourcesListItem
                        resource={linkedResource}
                        className="!space-y-0"
                        isLinkedResource={true}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Show actual content for unlocked resources
            <>
              {resource.description && (
                <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                  {resource.description}
                </p>
              )}
              {type === ResourceType.Pdf && (
                <iframe
                  src={url}
                  className="h-96 w-full rounded border-0"
                  title={title || "PDF Document"}
                  onLoad={trackResourceView}
                />
              )}
              {type === ResourceType.Image && (
                <img
                  src={url}
                  alt={title || "Image"}
                  className="h-auto max-h-96 w-full rounded object-contain"
                  onLoad={trackResourceView}
                />
              )}
              {type === ResourceType.Video && (
                <ResourceVideo resource={resource} />
              )}
              {type === ResourceType.Youtube && (
                <ResourceYoutubeVideo resource={resource} />
              )}
              {type === ResourceType.Url && <ResourceUrl resource={resource} />}
              {type === ResourceType.Assessment && (
                <ResourceAssessment
                  resource={resource}
                  isLinkedResource={isLinkedResource}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
